/**
 * Widget feedback transport (spec-traces-and-feedback 05a) — the two widget-token routes 04b
 * serves on unified-chat-service:
 *
 *   POST   /conversations/widget/:contactId/feedback            → 201 { data: { signalId, truncated } }
 *   DELETE /conversations/widget/:contactId/feedback/:signalId?conversationId=
 *                                                               → 200 { data: { signalId, status } }
 *
 * `axiosChat` already sends `Authorization: Bearer <widgetToken>`; both routes are TOKEN-ONLY.
 * The body never names a tenant, bot or contact — the edge binds all three to the token.
 *
 * No silent retry (§8 "do not retry a failed submit silently"): the shared response interceptor
 * retries every failure up to three times, which on these routes would spend the per-conversation
 * rate budget (10/60 s, shared by submit and withdraw) on requests the visitor never made. The
 * interceptor's own marker, `__retryCount: 3`, switches it off for these two calls only.
 */
import { axiosChat } from "../configs/http-config";
import { SessionManager } from "../session/SessionManager";

const NO_RETRY = Object.freeze({ __retryCount: 3 });

/** The contact id form the widget already sends to chat (Chat.jsx's history read). */
const contactPath = () => SessionManager.getSocketContactId()?.replace(/(_[^_]+)\1$/, "$1");

const requireContact = () => {
	const contactId = contactPath();
	if (!contactId) throw Object.assign(new Error("no contact"), { code: "NO_CONTACT" });
	return encodeURIComponent(contactId);
};

/**
 * Rate a reply (or re-rate it: a flip, or a chips Send on the same thumb — the edge supersedes the
 * prior rating itself). `freeText` is sent only when given.
 * @returns {Promise<{signalId?: string, truncated?: boolean}>}
 */
export async function submitFeedback({ conversationId, recordId, rating, chips = [], freeText }) {
	const contactId = requireContact();
	const body = { conversationId, recordId, rating, chips, ...(freeText ? { freeText } : {}) };
	const res = await axiosChat.post(`/conversations/widget/${contactId}/feedback`, body, { ...NO_RETRY });
	return res?.data?.data || {};
}

/** Take a rating back (same thumb twice). */
export async function withdrawFeedback({ signalId, conversationId }) {
	const contactId = requireContact();
	await axiosChat.delete(`/conversations/widget/${contactId}/feedback/${encodeURIComponent(signalId)}`, {
		params: { conversationId },
		...NO_RETRY,
	});
}

/** What the row does with a failure. Each value is also the `feedback.error_<key>` locale key, except `gone`. */
export const FEEDBACK_ERROR = Object.freeze({
	UNAVAILABLE: "unavailable", // hide the row for this record (403 / 401 / 404 CAPTURE_DISABLED)
	RATE_LIMITED: "rate_limited", // disable the thumbs for the cooldown (429)
	INVALID: "invalid", // 400 — a chip/text/rating the edge refused
	RETRY_LATER: "retry_later", // 503 / network / anything else — retryable, never queued
	GONE: "gone", // DELETE only: 404 NOT_FOUND / 409 NOT_OPEN — the rating is already cleared
});

const GONE_CODES = new Set(["FEEDBACK_NOT_FOUND", "FEEDBACK_NOT_OPEN"]);

/**
 * Map a failed call onto what the row does. Localized on the body's `code` where it decides
 * (a 404 is CAPTURE_DISABLED on either route but NOT_FOUND only on a withdraw), else the status.
 */
export const errorKeyFor = (err) => {
	const status = err?.response?.status;
	const code = err?.response?.data?.code;
	if (GONE_CODES.has(code) || status === 409) return FEEDBACK_ERROR.GONE;
	if (status === 401 || status === 403 || status === 404) return FEEDBACK_ERROR.UNAVAILABLE;
	if (status === 429) return FEEDBACK_ERROR.RATE_LIMITED;
	if (status === 400) return FEEDBACK_ERROR.INVALID;
	return FEEDBACK_ERROR.RETRY_LATER;
};
